import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingWestBeachPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Painters West Beach - Beachfront Painting Specialists | Wind-Resistant Coatings',
    description: 'Expert beachfront painting services in West Beach, Bloubergstrand, and Table View. Specialized in wind-resistant coatings and sand protection. Over 13 years protecting properties from wind and sand damage.',
    keywords: 'painters West Beach, beachfront painting West Beach, wind-resistant coatings, sand protection, coastal painting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters West Beach',
          description: 'Beachfront painting with wind-resistant coatings',
          address: 'West Beach, Cape Town',
          priceRange: '$$',
          serviceArea: ['West Beach', 'Bloubergstrand', 'Table View', 'Big Bay', 'Sunningdale', 'Parklands']
        }),
        generateServiceSchema({
          name: 'Beachfront Painting Services',
          description: 'Specialized wind-resistant and sand protection coatings',
          provider: 'Painters West Beach',
          areaServed: ['West Beach', 'Bloubergstrand', 'Table View'],
          serviceType: ['Beachfront Painting', 'Wind-Resistant Coatings', 'Sand Protection']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters West Beach",
    location: "West Beach, Cape Town",
    website: "http://painterswestbeach.co.za",
    heroImage: "https://public.readdy.ai/ai/img_res/bacaff1226bf26f1eb2d2cfc1f9b6c36.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=freshly%20painted%20beachfront%20house%20exterior%20with%20white%20walls%20and%20ocean%20views%20in%20West%20Beach%20professional%20finish%20with%20sand%20protection%20coating%20blue%20sky%20and%20beach%20background&width=800&height=600&seq=westbeachgallery1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=interior%20room%20painting%20in%20beachfront%20home%20with%20professional%20painter%20applying%20paint%20to%20walls%20using%20roller%20clean%20workspace%20with%20drop%20cloths%20and%20painting%20supplies%20ocean%20view%20through%20windows&width=800&height=600&seq=westbeachgallery2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=coastal%20property%20exterior%20painting%20project%20with%20scaffolding%20professional%20painters%20working%20on%20beachfront%20structure%20with%20wind%20protection%20modern%20architecture&width=800&height=600&seq=westbeachgallery3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20coastal%20interior%20living%20room%20with%20feature%20wall%20elegant%20color%20scheme%20modern%20furniture%20beach%20views%20natural%20lighting%20professional%20finish&width=800&height=600&seq=westbeachgallery4&orientation=landscape"
    ],
    services: [
      {
        title: "Beachfront Painting",
        description: "Specialized painting services for beachfront properties. We understand the unique challenges of painting in direct beach environments and use premium coatings designed for maximum protection.",
        features: ["Beachfront homes", "Coastal apartments", "Beach houses", "Holiday properties"]
      },
      {
        title: "Wind-Resistant Coatings",
        description: "Premium quality wind-resistant paints and coatings specifically designed to withstand strong coastal winds, salt spray, and sand exposure.",
        features: ["Weather-resistant paints", "Wind protection", "UV resistance", "Long-lasting finishes"]
      },
      {
        title: "Sand Protection",
        description: "Expert protective coatings and surface treatments that shield your property from sand damage and abrasion. We prepare surfaces properly and apply specialized coatings.",
        features: ["Sand-resistant coatings", "Surface protection", "Easy-clean finishes", "Preventive maintenance"]
      }
    ],
    serviceAreas: ["West Beach", "Bloubergstrand", "Table View", "Big Bay", "Sunningdale", "Parklands"],
    about: "Painters West Beach is a leading beachfront painting company serving West Beach and the surrounding Atlantic Seaboard areas. With over 13 years of experience in beachfront property painting, we have established ourselves as the most trusted painting contractors for beach properties in the area. Our team of skilled professionals specializes in wind-resistant coatings and sand protection.",
    aboutExtended: [
      "We understand the unique challenges that beachfront properties face, including strong winds, salt spray, sand abrasion, and intense UV exposure. That's why we exclusively use premium wind-resistant paints and coatings that are specifically formulated to withstand these harsh conditions. Our expertise extends across beachfront homes, coastal apartments, beach houses, and holiday properties.",
      "What sets us apart is our deep understanding of beachfront painting requirements and our commitment to using only the highest quality wind-resistant and sand-protective materials. Our painters are fully trained in coastal painting techniques, surface preparation for sand-exposed areas, and the application of protective coatings. We're fully licensed and insured, giving you complete peace of mind throughout your project.",
      "We take pride in our meticulous approach to every beachfront project. From initial assessment and surface preparation to final protective coating application, we ensure that your beach property is not only beautifully painted but also protected against wind, sand, and salt for years to come. Whether you're looking to refresh your beachfront home or protect your coastal investment, we deliver exceptional results that withstand the elements."
    ],
    ourApproach: [
      "Comprehensive beachfront property assessment",
      "Wind and sand exposure evaluation",
      "Thorough surface preparation and cleaning",
      "Premium wind-resistant paints and coatings",
      "Professional application techniques",
      "Sand-protective sealing and finishing",
      "Post-project inspection and warranty"
    ],
    whyChoose: [
      "13+ years beachfront painting experience",
      "Wind-resistant coating specialists",
      "Sand protection experts",
      "Fully licensed and insured",
      "Free beachfront property assessment",
      "Weather-resistant materials",
      "Competitive pricing",
      "Satisfaction guaranteed"
    ],
    testimonials: [
      {
        name: "Michael Stevens",
        rating: 5,
        text: "Outstanding service! They really understand beachfront painting. Our beach house looks amazing and we can see the difference in how well it's protected from the wind and sand."
      },
      {
        name: "Linda Botha",
        rating: 5,
        text: "Professional team that knows what they're doing. They applied wind-resistant coatings and sand protection to our property. Very impressed with the quality and durability."
      },
      {
        name: "Peter Williams",
        rating: 5,
        text: "Best beachfront painters in West Beach! They were thorough, professional, and the results are outstanding. Our home has never looked better and feels well protected."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters West Beach</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters West Beach</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas in the Atlantic Seaboard:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Beachfront Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer specialized beachfront painting services designed to protect and beautify your beach property.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">West Beach, Bloubergstrand, Table View</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: 9AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>West Beach</li>
                <li>Bloubergstrand</li>
                <li>Table View</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by leaderr
              </a>
              <span>|</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
